import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import Button from "../../Button";
import { useRegistrationForm } from "../core/RegistrationFormProvider";
import logo from "../../../storage/logo.png";

const validationSchema = Yup.object({
    phone: Yup.string()
        .required("שדה חובה")
        .matches(/^[0-9]+$/, "מספר טלפון לא תקין. יש להקליד ספרות בלבד.")
        .length(10, "מספר טלפון חייב להיות בעל 10 ספרות"),
    id4: Yup.string()
        .required("שדה חובה")
        .matches(/^[0-9]+$/, "יש להקליד ספרות בלבד")
        .length(4, "יש להקליד 4 ספרות אחרונות של תעודת זהות"),
});

export default function ValidationTab({ onSuccess }) {
    const { isLoading, registerData, setRegisterData, startTimer } = useRegistrationForm();


	useEffect(() => {
		if (!registerData) {
			return;
		}

		Object.keys(validationSchema.fields).forEach((field) => {
			if (registerData.hasOwnProperty(field)) {
				formik.setFieldValue(field, registerData[field]);
			}
		});
	}, []);

    const onSubmit = async (values) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}checkUserIdPhone`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(values),
            });

            const { success, message, childs, digits, parent_id, mobile_number, parent_name, booked } = await response.json();
   
            if (!success) {
                formik.setErrors({
                    phone: message || "הפרטים שהוזנו אינם נכונים",
                });
                return;
            }

			onSuccess?.();
			startTimer();

            await fetch(`${process.env.REACT_APP_API_URL}clean-temp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    parent_id: String(parent_id),
                    digits
                }),
            });

            setRegisterData({
                ...values,
                childs,
                digits,
                parent_id,
                mobile_number,
                parent_name,
                booked,
                step: 1,
            });
        } catch (error) {
            console.error("Error validating user:", error);
            formik.setErrors({
                phone: "אירעה שגיאה, אנא נסה שוב",
            });
        }
    };

    const formik = useFormik({
        validationSchema,
        initialValues: {
            phone: "",
            id4: "",
        },
        onSubmit,
    });

    const { errors, touched, isSubmitting } = formik;

    return (
        <div className="max-w-md w-full mx-auto my-auto pt-20 pb-5 text-center rounded-xl bg-opacity-30 bg-white text-white relative">
            <header className="absolute top-[-67.5px] w-full">
                <img src={logo} alt="logo" className="mx-auto" />
            </header>
            <h2 className="text-2xl font-bold mb-8">רישום לקייטנת אקשן על גלגלים - בית חולים שיבא</h2>
            <form onSubmit={formik.handleSubmit} className="mx-8">
                <div className="grid md:gap-8 text-right mx-auto text-md w-full">
                    <div className="col-span-1">
                        {/* Phone */}
                        <div className="mb-4 flex gap-4">
                            <div className="w-full md:flex items-center gap-2">
                                <div className="w-full md:w-1/5 pb-4">
                                    <label className="font-semibold" htmlFor="phone">
                                        טלפון
                                    </label>
                                </div>
                                <div className="relative w-full md:w-4/5 pb-4">
                                    <input
                                        id="phone"
                                        {...formik.getFieldProps("phone")}
                                        disabled={isLoading || isSubmitting}
                                        className={`px-4 py-3 rounded-2xl w-full text-black text-sm border-2 ${touched && touched.phone && errors && errors.phone ? "border-red-600" : "border-transparent"}`}
                                        type="text"
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, '');
                                            formik.setFieldValue("phone", value);
                                        }}
                                        maxLength={10}
                                    />
                                    {touched && touched.phone && errors && errors.phone && (
                                        <span className="block absolute bottom-0 right-0 text-xs leading-none text-red-700">
                                            {errors.phone}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Last 4 digits of ID */}
                        <div className="mb-4 flex gap-4">
                            <div className="w-full md:flex items-center gap-2">
                                <div className="w-full md:w-1/5 pb-4">
                                    <label className="font-semibold" htmlFor="id4">
                                        4 ספרות אחרונות של ת.ז
                                    </label>
                                </div>
                                <div className="relative w-full md:w-4/5 pb-4">
                                    <input
                                        id="id4"
                                        {...formik.getFieldProps("id4")}
                                        disabled={isLoading || isSubmitting}
                                        className={`px-4 py-3 rounded-2xl w-full text-black text-sm border-2 ${touched && touched.id4 && errors && errors.id4 ? "border-red-600" : "border-transparent"}`}
                                        type="text"
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, '');
                                            formik.setFieldValue("id4", value);
                                        }}
                                        maxLength={4}
                                    />
                                    {touched && touched.id4 && errors && errors.id4 && (
                                        <span className="block absolute bottom-0 right-0 text-xs leading-none text-red-700">
                                            {errors.id4}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-4">
                    {errors.length > 0 && (
                        <div className="text-red-600 mb-4">
                            {errors.map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </div>
                    )}
                    <Button type="submit" disabled={isLoading || isSubmitting}>
                        המשך
                    </Button>
                </div>
            </form>
        </div>
    );
} 